// src/components/forms/SignatureForm.jsx
import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";

const dataURLtoBlob = (dataURL) => {
  const [header, b64] = dataURL.split(",");
  const mime = header.match(/:(.*?);/)[1];
  const bin = atob(b64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return new Blob([u8], { type: mime });
};

export default function SignatureForm({
  value = "",
  onChange,
  width = 300,
  height = 300,
  className = "",
}) {
  const sigRef = useRef(null);
  const [mode, setMode] = useState(value ? "image" : "canvas"); // "canvas" | "image"
  const [preview, setPreview] = useState(value || "");
  const [fileObj, setFileObj] = useState(null); // File | Blob | null
  const previousObjectUrl = useRef(null);

  // 외부 value 변화 반영
  useEffect(() => {
    if (value) {
      setPreview(value);
      setFileObj(null);
      setMode("image");
    } else {
      setPreview("");
      setFileObj(null);
      setMode("canvas");
    }
  }, [value]);

  // objectURL 정리
  useEffect(() => {
    return () => {
      if (previousObjectUrl.current) {
        URL.revokeObjectURL(previousObjectUrl.current);
        previousObjectUrl.current = null;
      }
    };
  }, []);

  const emitChange = (url, blobOrFile) => {
    onChange?.(url, blobOrFile ?? null);
  };

  const handleClear = () => {
    sigRef.current?.clear();
    setPreview("");
    setFileObj(null);
    setMode("canvas");
    emitChange("", null);
  };

  const handleSaveCanvas = () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      alert("서명이 없습니다.");
      return;
    }
    const dataURL = sigRef.current.toDataURL("image/png");
    const blob = dataURLtoBlob(dataURL);
    setPreview(dataURL);
    setFileObj(blob);
    setMode("image");
    emitChange(dataURL, blob);
  };

  const handleFile = (file) => {
    if (!file) return;
    // 이전 objectURL 정리
    if (previousObjectUrl.current) {
      URL.revokeObjectURL(previousObjectUrl.current);
      previousObjectUrl.current = null;
    }
    const url = URL.createObjectURL(file);
    previousObjectUrl.current = url;

    setPreview(url);
    setFileObj(file);
    setMode("image");
    emitChange(url, file);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {mode === "canvas" && (
        <div>
          <div
            className="relative border border-gray-300 bg-white"
            style={{ width, height }}
          >
            {/* key로 강제 재마운트 */}
            <SignatureCanvas
              key={`canvas-${mode}`}
              ref={sigRef}
              penColor="black"
              canvasProps={{ className: "w-full h-full", width, height }}
            />
            <button
              type="button"
              onClick={handleClear}
              className="absolute top-2 right-2 px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
            >
              Clear
            </button>
          </div>

          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={handleSaveCanvas}
              className="rounded-md border px-3 py-1 hover:bg-gray-50"
            >
              임시 저장
            </button>

            <label className="rounded-md border px-3 py-1 hover:bg-gray-50 cursor-pointer">
              이미지 업로드
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>
          </div>
        </div>
      )}

      {mode === "image" && preview && (
        <div>
          <img
            key={`img-${preview}`} // 새 URL일 때 강제 새로 그림
            src={preview}
            alt="서명 미리보기"
            className="w-[120px] h-[120px] object-contain border border-gray-300"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={handleClear}
              className="rounded-md border px-3 py-1 hover:bg-gray-50"
            >
              서명 다시하기
            </button>

            <label className="rounded-md border px-3 py-1 hover:bg-gray-50 cursor-pointer">
              이미지 변경
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
